import { BrandDNASchema, type BrandDNA } from '@/entities/brand';
import type { ReplicateClient } from '@/shared/api/replicate';
import { parseJsonLoose, runLlm } from '@/shared/api/llm';

const SYSTEM_PROMPT = `You are a brand research and creative direction expert.
Given a brand name and URL, output a structured Brand DNA JSON document.
Return ONLY valid JSON matching the schema described in the user message — no prose, no fences.

Determine productType from the homepage signals:
  - "app" → the brand sells software, a mobile/web app, or a SaaS where the product surface is a UI screen.
  - "physical" → the brand sells physical goods (CPG, apparel, hardware, food, beverage).
  - "service" → consultancy, agency, marketplace, or service business with no app and no shippable product.
For app brands, productDescription should describe the app's primary screen and visual identity (dark mode? card-based? what the user sees first), not bottle/box copy.`;

const USER_TEMPLATE = (name: string, url: string, scrapedSnippet: string) => `Brand: ${name}
URL: ${url}

Scraped homepage snippet (may be empty if blocked):
"""
${scrapedSnippet.slice(0, 4000)}
"""

Return a JSON object with these exact keys:
{
  "productType": "app | physical | service",
  "primaryColor": "#HEXCODE (name)",
  "accentColor": "#HEXCODE (name)",
  "background": "#HEXCODE",
  "typography": "Font family name and style",
  "photographyStyle": "Lifestyle/Studio/UGC/etc.",
  "photographyMood": "Bright & airy / Dark & moody / etc.",
  "productDescription": "Short description of the flagship product",
  "usp": "Main promise / unique selling proposition",
  "audience": "Demographics + key pain points",
  "headlineTone": "Direct/Emotional/Humorous/Scientific",
  "promptModifier": "50-75 word style preamble that will be prepended to every ad image prompt: hex colors, typography, photo mood, brand aesthetic"
}`;

export interface ResearchInput {
  brandName: string;
  brandUrl: string;
  scrapedSnippet: string;
  llmModelId: string;
  signal?: AbortSignal;
}

export interface ResearchOutput {
  dna: BrandDNA;
  inputTokens?: number;
  outputTokens?: number;
  predictTimeSec?: number;
  replicateId: string;
}

export async function researchBrandDna(
  client: ReplicateClient,
  input: ResearchInput,
): Promise<ResearchOutput> {
  const llm = await runLlm(client, {
    modelId: input.llmModelId,
    systemPrompt: SYSTEM_PROMPT,
    prompt: USER_TEMPLATE(input.brandName, input.brandUrl, input.scrapedSnippet),
    maxTokens: 4096,
    temperature: 0.4,
    ...(input.signal && { signal: input.signal }),
  });

  const dna = parseJsonLoose(llm.text, BrandDNASchema);
  return {
    dna,
    replicateId: llm.replicateId,
    ...(llm.inputTokens !== undefined && { inputTokens: llm.inputTokens }),
    ...(llm.outputTokens !== undefined && { outputTokens: llm.outputTokens }),
    ...(llm.predictTimeSec !== undefined && { predictTimeSec: llm.predictTimeSec }),
  };
}

/**
 * Fetches a homepage HTML snippet. Throws on CORS / network / non-2xx so the
 * caller can refuse to spawn a research job against a URL we couldn't read —
 * silently feeding an empty snippet to the LLM produces hallucinated DNA.
 */
export async function fetchBrandSnippet(url: string, signal?: AbortSignal): Promise<string> {
  // Always go through the same-origin /scrape proxy (Cloudflare Pages function
  // in prod, Vite middleware in dev) so we don't run into CORS for sites that
  // don't allow cross-origin XHR — which is most of the web.
  const proxied = `/scrape?url=${encodeURIComponent(url)}`;
  let resp: Response;
  try {
    const opts: RequestInit = {};
    if (signal) opts.signal = signal;
    resp = await fetch(proxied, opts);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Network error while fetching ${url}: ${msg}.`);
  }
  if (!resp.ok) {
    const detail = (await resp.text().catch(() => '')).slice(0, 200);
    throw new Error(
      `Fetching ${url} via proxy failed (HTTP ${resp.status})${detail ? `: ${detail}` : ''}.`,
    );
  }
  const html = await resp.text();
  const text = html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 8000);
  if (text.length === 0) {
    throw new Error(`Fetched ${url} but extracted no readable content.`);
  }
  return text;
}
